import axios from 'axios';
import { BASE_URL } from '../constants/api-urls';
import { getAuth0AuthHeaders } from '../utils/auth0Session';
import type { CampaignAcceptPlaylistOption } from '../utils/campaignAcceptPlaylists';
import type {
  AccountCuratorOfferSubmitPayload,
  LinkedCuratorProfile,
} from '../utils/accountCuratorOffer';
import type { CuratorOfferSubmissionPlaylist } from '../utils/curatorOffer';

interface RawLinkedProfileResponse {
  _id: string;
  username: string;
  image?: string;
  acceptCampaigns?: boolean;
  curatorOffer?: LinkedCuratorProfile['curatorOffer'];
}

export const fetchLinkedCuratorProfiles = async (
  accountUserId: string
): Promise<LinkedCuratorProfile[]> => {
  const response = await axios.get(`${BASE_URL}/user/${encodeURIComponent(accountUserId)}/data`, {
    headers: getAuth0AuthHeaders(),
  });
  const profiles: RawLinkedProfileResponse[] = response?.data?.data || [];

  return profiles.map(profile => ({
    id: profile._id,
    username: profile.username,
    image: profile.image,
    acceptCampaigns: profile.acceptCampaigns === true,
    curatorOffer: profile.curatorOffer || null,
  }));
};

export const fetchProfileOwnedPlaylists = async (
  profileId: string,
  accountUserId: string
): Promise<CuratorOfferSubmissionPlaylist[]> => {
  const response = await axios.get(
    `${BASE_URL}/user/${encodeURIComponent(profileId)}/owned-playlists`,
    {
      params: { accountUserId },
      headers: getAuth0AuthHeaders(),
    }
  );

  return response?.data?.data || [];
};

export const fetchAccountOwnedPlaylists = async (
  accountUserId: string
): Promise<CampaignAcceptPlaylistOption[]> => {
  const response = await axios.get(`${BASE_URL}/user/account/owned-playlists`, {
    params: { accountUserId },
    headers: getAuth0AuthHeaders(),
  });

  return response?.data?.data || [];
};

export const fetchProfileSpotifySession = async (
  profileId: string,
  accountUserId: string
): Promise<{
  accessToken: string;
  spotifyUserId: string;
}> => {
  const response = await axios.get(
    `${BASE_URL}/user/${encodeURIComponent(profileId)}/spotify-session`,
    {
      params: { accountUserId },
      headers: getAuth0AuthHeaders(),
    }
  );

  const data = response?.data?.data || {};
  return {
    accessToken: data.accessToken,
    spotifyUserId: data.spotifyUserId,
  };
};

export const saveAccountCuratorOffers = async (payload: AccountCuratorOfferSubmitPayload) => {
  const response = await axios.put(`${BASE_URL}/user/account/curator-offers`, payload, {
    headers: getAuth0AuthHeaders(),
  });
  return response?.data?.data || [];
};
